import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Generic client-side pagination state hook + UI component.
 *
 * Usage:
 *   const { pageItems, controls, setPage } = usePagination(items, { pageSize: 25 });
 *   {pageItems.map(...)}
 *   {controls}
 *
 * Keeps state simple (current page + page size). Resets to page 1 whenever
 * the underlying list length or `resetKey` changes (e.g. a filter changed).
 */
export function usePagination(items, { pageSize: initialPageSize = 25, resetKey = null, label = "items" } = {}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Snap the current page back into range when filters shrink the list.
  useEffect(() => { setPage(1); }, [resetKey, pageSize]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  const start = (page - 1) * pageSize;
  const pageItems = useMemo(() => items.slice(start, start + pageSize), [items, start, pageSize]);
  const showingFrom = total === 0 ? 0 : start + 1;
  const showingTo = Math.min(start + pageSize, total);

  const controls = (
    <Pagination
      page={page}
      totalPages={totalPages}
      pageSize={pageSize}
      total={total}
      showingFrom={showingFrom}
      showingTo={showingTo}
      label={label}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
    />
  );

  return { page, pageSize, totalPages, pageItems, controls, setPage, setPageSize };
}

export default function Pagination({
  page, totalPages, pageSize, total, showingFrom, showingTo, label = "items",
  onPageChange, onPageSizeChange, sizes = [25, 50, 100, 200],
}) {
  if (total === 0) return null;

  // Build a windowed list of page numbers e.g. [1, ..., 3, 4, 5, ..., 12]
  const pages = [];
  const win = 1; // neighbours on either side of current
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - win && p <= page + win)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  return (
    <div
      className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 px-2 py-2 text-sm text-slate-600"
      data-testid="pagination"
    >
      <div className="flex items-center gap-2">
        <span data-testid="pagination-range">
          Showing <strong>{showingFrom}</strong>–<strong>{showingTo}</strong> of <strong>{total}</strong> {label}
        </span>
        {onPageSizeChange && (
          <label className="hidden sm:inline-flex items-center gap-1.5 ml-3">
            <span className="text-xs text-slate-500">Per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30"
              data-testid="pagination-page-size"
            >
              {sizes.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button" onClick={() => onPageChange(1)} disabled={page === 1}
          aria-label="First page" data-testid="pagination-first"
          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        ><ChevronsLeft className="w-4 h-4" /></button>
        <button
          type="button" onClick={() => onPageChange(page - 1)} disabled={page === 1}
          aria-label="Previous page" data-testid="pagination-prev"
          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        ><ChevronLeft className="w-4 h-4" /></button>
        {pages.map((p, idx) => p === "…" ? (
          <span key={`e-${idx}`} className="px-1.5 text-slate-400 select-none">…</span>
        ) : (
          <button
            key={p} type="button" onClick={() => onPageChange(p)}
            data-testid={`pagination-page-${p}`}
            className={`min-w-[2rem] h-8 px-2 rounded-lg text-xs font-semibold transition ${
              p === page
                ? "bg-[#2563EB] text-white shadow-sm"
                : "border border-slate-200 hover:bg-slate-50 text-slate-700"
            }`}
            aria-current={p === page ? "page" : undefined}
          >{p}</button>
        ))}
        <button
          type="button" onClick={() => onPageChange(page + 1)} disabled={page === totalPages}
          aria-label="Next page" data-testid="pagination-next"
          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        ><ChevronRight className="w-4 h-4" /></button>
        <button
          type="button" onClick={() => onPageChange(totalPages)} disabled={page === totalPages}
          aria-label="Last page" data-testid="pagination-last"
          className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        ><ChevronsRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
